/**
 * Ads Manager filter — model, validity, counting, storage (TZ_Code_AdsManager_Filter).
 *
 * Pure: no React, no network. The modal edits a `draft` of this shape, the
 * page keeps the `applied` one, and the server receives `toApiParam()`.
 * Every number the UI shows (tab badges, funnel counters, "Will be
 * applied", chips, empty state, Apply (N)) comes from `counts()` — one rule.
 */

export type Connector = "AND" | "OR" | null;
export type MetricOp = "gt" | "lt" | "eq" | "ne";
export type LabelOp = "contains" | "not_contains";
export type CountryOp = "in" | "not_in";
export type RuleOp = "applied" | "not_applied";

export type MetricCondition = { metric: string; op: MetricOp | ""; value: number | null; connector: Connector };
export type LabelCondition = { op: LabelOp; labelIds: number[]; connector: Connector };
export type CountryCondition = { op: CountryOp; countries: string[]; connector: Connector };
/** fix2: rule ids are the `rules.id` integers, exactly like `labelIds`. */
export type RuleCondition = { op: RuleOp; ruleIds: number[]; connector: Connector };

export type FilterTab = "metrics" | "label" | "country" | "rules";
export const FILTER_TABS: readonly FilterTab[] = ["metrics", "label", "country", "rules"];
export const TAB_LABEL: Record<FilterTab, string> = { metrics: "Metrics", label: "Label", country: "Country", rules: "Rules" };

export type AdsManagerFilter = {
  v: 3;
  metrics: MetricCondition[];
  label: LabelCondition[];
  country: CountryCondition[];
  /** fix2 — absent in filters stored before it; read as []. */
  rules: RuleCondition[];
};

export function emptyFilter(): AdsManagerFilter {
  return { v: 3, metrics: [], label: [], country: [], rules: [] };
}

export function blankMetric(): MetricCondition {
  return { metric: "", op: "", value: null, connector: null };
}
export function blankLabel(): LabelCondition {
  return { op: "contains", labelIds: [], connector: null };
}
export function blankCountry(): CountryCondition {
  return { op: "in", countries: [], connector: null };
}
export function blankRule(): RuleCondition {
  return { op: "applied", ruleIds: [], connector: null };
}

function connectorOk(index: number, c: Connector): boolean {
  return index === 0 || c === "AND" || c === "OR";
}

/** TZ §2: metric + op + finite value; a 2nd+ row also needs its connector. */
export function metricRowValid(r: MetricCondition, index: number): boolean {
  return !!r.metric && r.op !== "" && r.value != null && Number.isFinite(r.value) && connectorOk(index, r.connector);
}
export function labelRowValid(r: LabelCondition, index: number): boolean {
  return r.labelIds.length > 0 && connectorOk(index, r.connector);
}
export function countryRowValid(r: CountryCondition, index: number): boolean {
  return r.countries.length > 0 && connectorOk(index, r.connector);
}
export function ruleRowValid(r: RuleCondition, index: number): boolean {
  return r.ruleIds.length > 0 && connectorOk(index, r.connector);
}

/** Drop invalid rows. What survives is what gets applied, counted and stored. */
export function normalize(f: AdsManagerFilter): AdsManagerFilter {
  return {
    v: 3,
    metrics: f.metrics.filter(metricRowValid).map((r) => ({ ...r })),
    label: f.label.filter(labelRowValid).map((r) => ({ ...r, labelIds: [...r.labelIds] })),
    country: f.country
      .filter(countryRowValid)
      .map((r) => ({ ...r, countries: r.countries.map((c) => c.trim().toUpperCase()).filter(Boolean) })),
    rules: (f.rules ?? []).filter(ruleRowValid).map((r) => ({ ...r, ruleIds: [...r.ruleIds] })),
  };
}

export type FilterCounts = Record<FilterTab, number>;

/** TZ §4 — the one counting rule: metric rows; selected labels; selected
 *  countries; selected rules (fix2, as labels). */
export function counts(f: AdsManagerFilter): FilterCounts {
  const n = normalize(f);
  return {
    metrics: n.metrics.length,
    label: n.label.reduce((s, r) => s + r.labelIds.length, 0),
    country: n.country.reduce((s, r) => s + r.countries.length, 0),
    rules: n.rules.reduce((s, r) => s + r.ruleIds.length, 0),
  };
}

export function totalCount(f: AdsManagerFilter): number {
  const c = counts(f);
  return c.metrics + c.label + c.country + c.rules;
}

export function isEmptyFilter(f: AdsManagerFilter): boolean {
  return totalCount(f) === 0;
}

export function activeTabs(f: AdsManagerFilter): FilterTab[] {
  const c = counts(f);
  return FILTER_TABS.filter((t) => c[t] > 0);
}

export function sameFilter(a: AdsManagerFilter, b: AdsManagerFilter): boolean {
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
}

export function clearType(f: AdsManagerFilter, tab: FilterTab): AdsManagerFilter {
  return { ...f, [tab]: [] };
}

// ---------------------------------------------------------------------------
// Storage — localStorage, `oneads:adsFilter:{accountId}:{appId|all}:{level}`
// (TZ §8 + fix1). The filter lives on the LEVEL it was enabled on and only
// there: campaigns / ad groups / keywords each keep their own, independent
// of each other and shared across parents (a filter set on ad groups inside
// campaign X applies to ad groups inside campaign Y).
// ---------------------------------------------------------------------------

/** Stable level ids from the Ads Manager routing — not tab captions. The
 *  other levels of the product (`searchterms`, `negatives`, `ads`) carry no
 *  filter, so no key is ever written for them. */
export type FilterLevel = "campaigns" | "adgroups" | "keywords";

export function storageKey(accountId: string, appId: number | "all", level: FilterLevel): string {
  return `${legacyStorageKey(accountId, appId)}:${level}`;
}

/** The pre-fix1 key (v2, no level) — read once by the migration, then removed. */
export function legacyStorageKey(accountId: string, appId: number | "all"): string {
  return `oneads:adsFilter:${accountId}:${appId === "all" ? "all" : String(appId)}`;
}

/** Read the level's filter, migrating a legacy v2 key (no level) into the
 *  `campaigns` level on the way: that is the level the old filter was
 *  enabled on, and where it kept leaking from. Runs once — the legacy key
 *  is deleted whether or not anything was carried over. */
export function loadLevelFilter(accountId: string, appId: number | "all", level: FilterLevel): AdsManagerFilter {
  migrateLegacy(accountId, appId);
  return loadFilter(storageKey(accountId, appId, level));
}

function migrateLegacy(accountId: string, appId: number | "all"): void {
  const legacy = legacyStorageKey(accountId, appId);
  let raw: string | null = null;
  try { raw = localStorage.getItem(legacy); } catch { return; }
  if (raw == null) return;
  const target = storageKey(accountId, appId, "campaigns");
  try {
    const parsed: unknown = JSON.parse(raw);
    const already = localStorage.getItem(target);
    if (already == null && parsed && typeof parsed === "object" && (parsed as { v?: unknown }).v === 2) {
      saveFilter(target, parseBody(parsed as Partial<AdsManagerFilter>));
    }
  } catch { /* unreadable legacy value — dropped below */ }
  try { localStorage.removeItem(legacy); } catch { /* ignore */ }
}

export function loadFilter(key: string): AdsManagerFilter {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return emptyFilter();
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || (parsed as { v?: unknown }).v !== 3) throw new Error("not v3");
    return parseBody(parsed as Partial<AdsManagerFilter>);
  } catch {
    try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
    return emptyFilter();
  }
}

function parseBody(p: Partial<AdsManagerFilter>): AdsManagerFilter {
  return normalize({
    v: 3,
    metrics: Array.isArray(p.metrics) ? p.metrics.map(asMetric) : [],
    label: Array.isArray(p.label) ? p.label.map(asLabel) : [],
    country: Array.isArray(p.country) ? p.country.map(asCountry) : [],
    rules: Array.isArray(p.rules) ? p.rules.map(asRule) : [],
  });
}

function asMetric(x: unknown): MetricCondition {
  const r = (x ?? {}) as Partial<MetricCondition>;
  const v = typeof r.value === "number" ? r.value : r.value == null ? null : Number(r.value);
  return { metric: typeof r.metric === "string" ? r.metric : "", op: isMetricOp(r.op) ? r.op : "", value: v, connector: asConnector(r.connector) };
}
function asLabel(x: unknown): LabelCondition {
  const r = (x ?? {}) as Partial<LabelCondition>;
  return { op: r.op === "not_contains" ? "not_contains" : "contains", labelIds: asIds(r.labelIds), connector: asConnector(r.connector) };
}
function asCountry(x: unknown): CountryCondition {
  const r = (x ?? {}) as Partial<CountryCondition>;
  return { op: r.op === "not_in" ? "not_in" : "in", countries: Array.isArray(r.countries) ? r.countries.filter((c): c is string => typeof c === "string") : [], connector: asConnector(r.connector) };
}
function asRule(x: unknown): RuleCondition {
  const r = (x ?? {}) as Partial<RuleCondition>;
  return { op: r.op === "not_applied" ? "not_applied" : "applied", ruleIds: asIds(r.ruleIds), connector: asConnector(r.connector) };
}
/** Integer ids from a stored array; `null` is not an id (`Number(null)` is 0). */
function asIds(xs: unknown): number[] {
  if (!Array.isArray(xs)) return [];
  return xs.filter((x) => typeof x === "number" || typeof x === "string").map(Number).filter(Number.isInteger);
}
function isMetricOp(op: unknown): op is MetricOp {
  return op === "gt" || op === "lt" || op === "eq" || op === "ne";
}
function asConnector(c: unknown): Connector {
  return c === "AND" || c === "OR" ? c : null;
}

export function saveFilter(key: string, f: AdsManagerFilter): void {
  const n = normalize(f);
  try {
    if (isEmptyFilter(n)) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(n));
  } catch { /* storage unavailable — the filter still applies for this session */ }
}

export function clearStoredFilter(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/** TZ §8 (+ fix2 §5): validate a loaded filter against what exists now — a
 *  retired metric drops its row; deleted labels / unknown ISO codes /
 *  deleted rules leave their arrays and an emptied row drops. `changed`
 *  says the pruned version must be written back. */
export function pruneFilter(
  f: AdsManagerFilter,
  known: { metrics: ReadonlySet<string>; labelIds: ReadonlySet<number>; countries: ReadonlySet<string>; ruleIds: ReadonlySet<number> },
): { filter: AdsManagerFilter; changed: boolean } {
  const before = JSON.stringify(normalize(f));
  const pruned: AdsManagerFilter = normalize({
    v: 3,
    metrics: f.metrics.filter((r) => known.metrics.has(r.metric)),
    label: f.label.map((r) => ({ ...r, labelIds: r.labelIds.filter((id) => known.labelIds.has(id)) })),
    country: f.country.map((r) => ({ ...r, countries: r.countries.filter((c) => known.countries.has(c)) })),
    rules: (f.rules ?? []).map((r) => ({ ...r, ruleIds: r.ruleIds.filter((id) => known.ruleIds.has(id)) })),
  });
  // A dropped row may leave the next one first with a connector — legal, ignored.
  return { filter: pruned, changed: JSON.stringify(pruned) !== before };
}

/** `?filter=` for the stats endpoints; undefined when nothing applies. */
export function toApiParam(f: AdsManagerFilter): string | undefined {
  const n = normalize(f);
  return isEmptyFilter(n) ? undefined : JSON.stringify(n);
}

// ---------------------------------------------------------------------------
// Readable summaries (chip tooltips, TZ §6)
// ---------------------------------------------------------------------------

export const METRIC_OP_LABEL: Record<MetricOp, string> = { gt: "greater than", lt: "less than", eq: "equals", ne: "not equals" };

export function describeType(
  f: AdsManagerFilter,
  tab: FilterTab,
  names: { metric: (key: string) => string; label: (id: number) => string; country: (iso: string) => string; rule: (id: number) => string },
): string {
  const n = normalize(f);
  const join = (parts: string[], rows: { connector: Connector }[]) =>
    parts.map((p, i) => (i === 0 ? p : `${rows[i].connector ?? "AND"} ${p}`)).join(" ");
  if (tab === "metrics") {
    return join(n.metrics.map((r) => `${names.metric(r.metric)} ${METRIC_OP_LABEL[r.op as MetricOp]} ${formatValue(r.value)}`), n.metrics);
  }
  if (tab === "label") {
    return join(n.label.map((r) => `Label ${r.op === "contains" ? "contains" : "does not contain"} ${r.labelIds.map(names.label).join(", ")}`), n.label);
  }
  if (tab === "country") {
    return join(n.country.map((r) => `Country ${r.op === "in" ? "is in" : "is not in"} ${r.countries.map(names.country).join(", ")}`), n.country);
  }
  return join(n.rules.map((r) => `Rules ${r.op === "applied" ? "applied" : "not applied"}: ${r.ruleIds.map(names.rule).join(", ")}`), n.rules);
}

function formatValue(v: number | null): string {
  if (v == null) return "";
  return Number.isInteger(v) ? String(v) : String(Number(v.toFixed(2)));
}

// ---------------------------------------------------------------------------
// Client-side evaluation — ONLY for rows the server never saw (ad groups
// added from the mirror with no stats in the period). Same grouping rule
// as the engine's `condition_groups`: AND binds tighter than OR, a missing
// connector reads as AND, any fully-true group matches.
// ---------------------------------------------------------------------------

export function andOrGroups<T>(items: { item: T; connector: Connector }[]): T[][] {
  const groups: T[][] = [];
  let cur: T[] = [];
  items.forEach(({ item, connector }, i) => {
    if (i > 0 && connector === "OR") { groups.push(cur); cur = []; }
    cur.push(item);
  });
  if (cur.length) groups.push(cur);
  return groups;
}

function anyGroup(results: { item: boolean; connector: Connector }[]): boolean {
  return andOrGroups(results).some((g) => g.every(Boolean));
}

export function compareMetric(op: MetricOp, actual: number | null, expected: number, unit: "count" | "money" | "pct" | "ratio"): boolean {
  if (actual == null || !Number.isFinite(actual)) return false; // NULL → false, `ne` included (TZ §3.2)
  let a = actual;
  let e = expected;
  if (unit !== "count" && (op === "eq" || op === "ne")) { a = Math.round(a * 100) / 100; e = Math.round(e * 100) / 100; }
  switch (op) {
    case "gt": return a > e;
    case "lt": return a < e;
    case "eq": return a === e;
    case "ne": return a !== e;
  }
}

export type RowFacts = {
  /** metric key → value in display units (percent already ×100), null when undefined */
  metric: (key: string) => { value: number | null; unit: "count" | "money" | "pct" | "ratio" } | undefined;
  labelIds: ReadonlySet<number>;
  countries: readonly string[];
  /** Ids of the rules the table's column shows on this row; `undefined`
   *  when the row never reached the server (a mirror-only ad group) — the
   *  verdict is then unknown and a Rules condition hides the row rather
   *  than guessing either way. */
  ruleIds?: ReadonlySet<number>;
};

/** Does a row pass the filter? Types AND; unknown metrics are skipped. */
export function rowMatches(f: AdsManagerFilter, facts: RowFacts): boolean {
  const n = normalize(f);
  const metricRows = n.metrics
    .map((r) => ({ r, m: facts.metric(r.metric) }))
    .filter((x) => x.m !== undefined);
  if (metricRows.length) {
    const ok = anyGroup(metricRows.map(({ r, m }, i) => ({
      item: compareMetric(r.op as MetricOp, m!.value, r.value as number, m!.unit),
      connector: i === 0 ? null : r.connector,
    })));
    if (!ok) return false;
  }
  if (n.label.length) {
    const ok = anyGroup(n.label.map((r, i) => {
      const has = r.labelIds.some((id) => facts.labelIds.has(id));
      return { item: r.op === "contains" ? has : !has, connector: i === 0 ? null : r.connector };
    }));
    if (!ok) return false;
  }
  if (n.country.length) {
    const ok = anyGroup(n.country.map((r, i) => {
      const overlap = facts.countries.some((c) => r.countries.includes(c));
      return { item: r.op === "in" ? overlap : !overlap, connector: i === 0 ? null : r.connector };
    }));
    if (!ok) return false;
  }
  if (n.rules.length) {
    const ids = facts.ruleIds;
    if (!ids) return false;
    const ok = anyGroup(n.rules.map((r, i) => {
      const has = r.ruleIds.some((id) => ids.has(id));
      return { item: r.op === "applied" ? has : !has, connector: i === 0 ? null : r.connector };
    }));
    if (!ok) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Facts for rows the server never saw: an ad group added from the mirror
// has no stats in the period, so every volume metric is 0 and every ratio
// is undefined (NULL). Same units as the backend's DSL_METRICS.
// ---------------------------------------------------------------------------

export const METRIC_UNITS: Record<string, "count" | "money" | "pct" | "ratio"> = {
  Impressions: "count", Taps: "count", TTR: "pct", "Average CPT": "money", Spend: "money",
  "Installs (Tap)": "count", "Installs (View)": "count", "Installs (Total)": "count",
  "CR (Tap)": "pct", "CR (Total)": "pct",
  "Average CPA (Tap)": "money", "Average CPA (View)": "money", "Average CPA (Total)": "money",
  "New downloads (Tap)": "count", "New downloads (View)": "count", "New downloads (Total)": "count",
  IPM: "ratio",
};

export function metricUnit(key: string): "count" | "money" | "pct" | "ratio" | undefined {
  if (key in METRIC_UNITS) return METRIC_UNITS[key];
  if (key.startsWith("event_cpa:")) return "money";
  if (key.startsWith("event_cr:")) return "pct";
  if (key.startsWith("event:")) return "count";
  return undefined;
}

export function zeroRowFacts(labelIds: ReadonlySet<number>, countries: readonly string[], ruleIds?: ReadonlySet<number>): RowFacts {
  return {
    metric: (key) => {
      const unit = metricUnit(key);
      if (!unit) return undefined;
      return { value: unit === "count" ? 0 : null, unit };
    },
    labelIds,
    countries,
    ruleIds,
  };
}
